import type { CSSProperties } from 'react'
import { getFontById, getFontWeightValue, FONT_OPTIONS, FONT_WEIGHT_OPTIONS } from './fonts'

export interface TextStyle {
  /** Taille en px (version ordinateur, reduite automatiquement sur mobile) */
  fontSize?: number
  /** Identifiant de police (voir FONT_OPTIONS) */
  fontFamily?: string
  /** Identifiant de graisse (light, regular, medium, semibold, bold) */
  fontWeight?: string
  italic?: boolean
}

export const TEXT_SIZE_OPTIONS = [12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 56, 64, 72, 96]

export function cleanTextStyle(style?: TextStyle | null): TextStyle {
  const result: TextStyle = {}
  if (!style) return result
  if (typeof style.fontSize === 'number' && style.fontSize > 0) result.fontSize = style.fontSize
  if (style.fontFamily && FONT_OPTIONS.some((f) => f.id === style.fontFamily)) result.fontFamily = style.fontFamily
  if (style.fontWeight && FONT_WEIGHT_OPTIONS.some((w) => w.id === style.fontWeight)) result.fontWeight = style.fontWeight
  if (style.italic) result.italic = true
  return result
}

export function sameTextStyle(a?: TextStyle | null, b?: TextStyle | null): boolean {
  return JSON.stringify(cleanTextStyle(a)) === JSON.stringify(cleanTextStyle(b))
}

/** Taille adaptative : pleine taille sur ordinateur, reduite sur petit ecran. */
export function responsiveSize(size: number): string {
  if (size <= 18) return `${size}px`
  const min = Math.max(16, Math.round(size * 0.6))
  const slope = (size * 0.1).toFixed(2)
  const base = Math.round(size * 0.5)
  return `clamp(${min}px, calc(${slope}vw + ${base}px), ${size}px)`
}

/** Convertit un style en variables CSS + attributs data-* lus par index.css */
export function textStyleToProps(input?: TextStyle | null): {
  attrs: Record<string, string>
  css: CSSProperties
} {
  const style = cleanTextStyle(input)
  const attrs: Record<string, string> = {}
  const css: Record<string, string> = {}

  if (style.fontSize) {
    attrs['data-ts-size'] = '1'
    css['--ts-size'] = responsiveSize(style.fontSize)
  }
  if (style.fontFamily) {
    attrs['data-ts-family'] = '1'
    css['--ts-family'] = getFontById(style.fontFamily).stack
  }
  if (style.fontWeight) {
    attrs['data-ts-weight'] = '1'
    css['--ts-weight'] = String(getFontWeightValue(style.fontWeight))
  }
  if (style.italic) {
    attrs['data-ts-italic'] = '1'
    css['--ts-italic'] = 'italic'
  }
  return { attrs, css: css as CSSProperties }
}
